import { t } from '@trimble/i18n';
import type { LengthUnit } from '@fulcrum/lib';
import { readModusInputChangeValue } from '@trimble/utilities';
import {
  Component,
  batch,
  createEffect,
  createSignal,
  on,
  onMount,
  Show,
} from 'solid-js';
import type { RgbColor } from 'solid-color';
import { ColorSwatch } from '../../components/ColorSwatch';
import {
  parseDisplayStopValueToMeters,
  STOP_ROW_HEIGHT_PX,
} from '../../heatmap-stop-layout';
import { PALETTE_STOP_NUMBER_INPUT_CLASS } from '../palette-stop-number-input-styles';
import { blockPaletteStopExponentKey } from '../palette-stop-value-input';
import { PaletteStopColorPicker } from './PaletteStopColorPicker';
import { createPaletteStopColorPickerControls } from './createPaletteStopColorPickerControls';

const EDITABLE_STOP_VALUE_INPUT_CLASS =
  'block mb-0 h-7 max-h-7 min-h-0 w-full min-w-0 max-w-full overflow-hidden [&_.modus-wc-input]:mb-0 [&_.modus-wc-input]:box-border [&_.modus-wc-input]:h-full [&_.modus-wc-input]:max-h-full [&_.modus-wc-input]:w-full [&_.modus-wc-input]:max-w-full [&_.modus-wc-input]:min-w-0 [&_.modus-wc-input]:!min-h-0 [&_.modus-wc-input-label]:mb-0 [&_.modus-wc-input-label]:pb-0 [&_input]:box-border [&_input]:h-7 [&_input]:max-h-7 [&_input]:min-h-0 [&_input]:min-w-0 [&_input]:max-w-full [&_input]:py-0 [&_input]:text-sm [&_input]:leading-7';

/** Modus only applies the error border class when `feedback` is set, which also
 * renders a feedback row that gets clipped by the fixed-height stop row slot. */
const DUPLICATE_STOP_VALUE_INPUT_CLASS = 'modus-wc-input--error';

const READONLY_STOP_VALUE_CLASS =
  'palette-stop-value-readonly block truncate self-center box-border w-full min-w-0 h-7 px-2 rounded bg-[var(--modus-wc-color-base-100,#f1f1f6)] text-sm leading-7 text-[var(--modus-wc-color-base-content,#171c1e)] cursor-default select-text';

export interface StopValueRowProps {
  readonly color: string;
  /** Committed stop value from the parent; seeds and resets the editable draft. */
  readonly initialValue: string;
  readonly lengthUnit: LengthUnit;
  readonly testId: string;
  readonly editable: boolean;
  readonly removeDisabled?: boolean;
  readonly hasError?: boolean;
  /** Highlights the swatch when the stop is active (after bar insert or handle activate). */
  readonly selected?: boolean;
  /** Focuses and selects the value input (e.g. after inserting from the gradient bar). */
  readonly focusValueInput?: boolean;
  /** Parent-owned display override while dragging a handle on the gradient bar. */
  readonly valueDisplayOverride?: string;
  /** Called when the editable value input receives focus. */
  onValueInputFocus?(): void;
  /** Called when the user saves a new colour from the swatch colour picker. */
  onColorChange(color: string): void;
  /** Called on Enter or blur with the committed draft value. */
  onValueCommit(value: string): void;
  /** Reports whether the local draft parses as a valid stop value. */
  onDraftValidityChange?(valid: boolean): void;
  onRemove(): void;
}

/**
 * Palette stop row: colour swatch and value. Pass `editable={false}` for
 * read-only display; `editable={true}` turns the value into an input and adds an
 * `×` remove control. Editable rows own a local draft while typing; the parent
 * is notified only on Enter or blur so row order does not change mid-edit (e.g.
 * "-" before "-0.5"). In editable mode, clicking the swatch opens a colour
 * picker. `initialValue` is the committed value from the parent and resets the
 * draft when it changes.
 */
export const StopValueRow: Component<StopValueRowProps> = (props) => {
  let valueInputHost: HTMLDivElement | undefined;

  const [draftValue, setDraftValue] = createSignal(props.initialValue);
  const [showInvalidDraftError, setShowInvalidDraftError] = createSignal(false);
  const colorPicker = createPaletteStopColorPickerControls();

  const isDraftValid = (): boolean =>
    parseDisplayStopValueToMeters(draftValue(), props.lengthUnit) !== null;

  const canPickColor = (): boolean => props.editable;

  function handleSwatchClick(): void {
    if (!canPickColor()) return;
    colorPicker.toggleColorPicker();
  }

  function handleColorChange(rgb: RgbColor): void {
    props.onColorChange(colorPicker.applyColor(rgb));
  }

  function handleColorSave(rgb: RgbColor): void {
    // `saveColor` closes the picker; batch that with the parent colour update
    // so both flush in a single reactive pass.
    batch(() => {
      const hex = colorPicker.saveColor(rgb);
      props.onColorChange(hex);
    });
  }

  function reportDraftValidity(value: string): void {
    if (!props.editable) return;
    props.onDraftValidityChange?.(
      parseDisplayStopValueToMeters(value, props.lengthUnit) !== null
    );
  }

  createEffect(
    on(
      () => [props.initialValue, props.valueDisplayOverride] as const,
      ([value, override]) => {
        if (override !== undefined) return;
        batch(() => {
          setDraftValue(value);
          setShowInvalidDraftError(false);
          reportDraftValidity(value);
        });
      }
    )
  );

  onMount(() => {
    reportDraftValidity(draftValue());
  });

  createEffect(
    on(
      () => props.focusValueInput === true,
      (shouldFocus) => {
        if (!shouldFocus || !props.editable) return;
        queueMicrotask(() => {
          const modusHost = valueInputHost?.querySelector(
            'modus-wc-number-input'
          );
          const input =
            modusHost?.shadowRoot?.querySelector('input') ??
            valueInputHost?.querySelector('input');
          if (input instanceof HTMLInputElement) {
            input.focus();
            input.select();
            valueInputHost?.setAttribute(
              'data-value-input-focus-requested',
              'true'
            );
            return;
          }
          if (modusHost instanceof HTMLElement) {
            modusHost.focus();
          }
          valueInputHost?.setAttribute(
            'data-value-input-focus-requested',
            'true'
          );
        });
      }
    )
  );

  // Enter commit can re-sort rows and drop focus synchronously (Solid `<For>`
  // reorder), which would otherwise fire focusout and commit a second time.
  let suppressFocusOutCommit = false;

  const displayedValue = (): string => {
    const override = props.valueDisplayOverride;
    if (override !== undefined) return override;
    return props.editable ? draftValue() : props.initialValue;
  };

  function handleInputChange(event: Event): void {
    if (!props.editable) return;
    const value = readModusInputChangeValue(event);
    batch(() => {
      setDraftValue(value);
      reportDraftValidity(value);
    });
  }

  function handleValueInputFocusIn(): void {
    props.onValueInputFocus?.();
  }

  function commitValue(): void {
    if (!props.editable) return;
    setShowInvalidDraftError(true);
    if (!isDraftValid()) return;

    suppressFocusOutCommit = true;
    props.onValueCommit(draftValue());
    queueMicrotask(() => {
      suppressFocusOutCommit = false;
    });
  }

  function handleKeyDown(event: KeyboardEvent): void {
    if (!props.editable) return;
    if (blockPaletteStopExponentKey(event)) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      commitValue();
    }
  }

  function handleFocusOut(): void {
    if (!props.editable || suppressFocusOutCommit) return;
    commitValue();
  }

  const hasInputError = (): boolean =>
    props.hasError === true || (showInvalidDraftError() && !isDraftValid());

  const inputErrorClass = (): string | undefined =>
    hasInputError() ? DUPLICATE_STOP_VALUE_INPUT_CLASS : undefined;

  return (
    <div
      class="flex w-full items-center gap-1.5"
      style={{ height: `${String(STOP_ROW_HEIGHT_PX)}px` }}
      data-testid={props.testId}
      data-selected={props.selected === true ? 'true' : undefined}
    >
      <div class="relative shrink-0" ref={colorPicker.setSwatchAnchor}>
        <ColorSwatch
          color={props.color}
          testId={`${props.testId}-swatch`}
          selected={
            props.selected === true ||
            (canPickColor() && colorPicker.showColorPicker())
          }
          onClick={canPickColor() ? handleSwatchClick : undefined}
        />
        <PaletteStopColorPicker
          show={colorPicker.showColorPicker() && canPickColor()}
          anchorEl={colorPicker.getSwatchAnchor()}
          color={props.color}
          testId={`${props.testId}-color-picker`}
          onChange={handleColorChange}
          onSave={handleColorSave}
          onClose={colorPicker.closeColorPicker}
        />
      </div>
      <div
        class="flex min-w-0 flex-1 items-center justify-start overflow-hidden max-w-[86px]"
        style={{ height: `${String(STOP_ROW_HEIGHT_PX)}px` }}
        data-testid={`${props.testId}-value`}
        data-has-error={hasInputError() ? 'true' : undefined}
        data-stop-value-error={
          showInvalidDraftError() && !isDraftValid() ? 'invalid' : undefined
        }
        ref={(el) => {
          valueInputHost = el;
        }}
        onFocusIn={handleValueInputFocusIn}
        onFocusOut={handleFocusOut}
        onKeyDown={handleKeyDown}
      >
        <Show
          when={props.editable}
          fallback={
            <span class={READONLY_STOP_VALUE_CLASS}>{props.initialValue}</span>
          }
        >
          <modus-wc-number-input
            class={`${EDITABLE_STOP_VALUE_INPUT_CLASS} ${PALETTE_STOP_NUMBER_INPUT_CLASS}`}
            attr:custom-class={inputErrorClass()}
            bordered={true}
            label=""
            size="sm"
            // @ts-expect-error -- HTML step="any" allows arbitrary decimals; Modus types step as number only
            step="any"
            value={displayedValue()}
            aria-invalid={hasInputError() ? true : undefined}
            on:inputChange={handleInputChange}
          />
        </Show>
      </div>
      <span
        class="text-sm text-gray-6 shrink-0 cursor-default"
        data-testid={`${props.testId}-unit`}
      >
        {props.lengthUnit}
      </span>
      <Show when={props.editable}>
        <modus-wc-tooltip
          content={t('measurements.volumetric.gradient.minColorStopsRequired')}
          position="top"
          disabled={props.removeDisabled !== true}
          class="shrink-0 [&_div]:flex"
        >
          <modus-wc-button
            type="button"
            color="tertiary"
            shape="square"
            size="sm"
            variant="borderless"
            class="shrink-0"
            onClick={() => props.onRemove()}
            // bool:disabled omits the attribute when false so Modus only treats the
            // button as disabled at the minimum stop count.
            // @ts-expect-error -- modus types do not support bool:disabled syntax
            bool:disabled={props.removeDisabled === true}
            attr:data-testid={`${props.testId}-remove`}
          >
            <modus-wc-icon name="close" size="sm" />
          </modus-wc-button>
        </modus-wc-tooltip>
      </Show>
    </div>
  );
};
